import getAllItemViewsByType from 'checkout/selectors/view/getAllItemViewsByType'
import {
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_EXPERIENCE,
  OFFER_TYPE_EXPERIENCE_TRANSFER,
  PRODUCT_TYPE_CAR_HIRE,
  PRODUCT_TYPE_TOUR_ULTRA_LUX,
  PRODUCT_TYPE_TOUR_PREMIUM,
  PRODUCT_TYPE_TOUR_DELUXE,
  PRODUCT_TYPE_TOUR_SIGNATURE_SERIES,
  PRODUCT_TYPE_TOUR_CUSTOM,
} from 'constants/offer'
import { arrayToMap, arrayToObject, sum, unique } from 'lib/array/arrayUtils'

import { match } from 'ts-pattern'
import { isTourV2Offer, isCarHireOffer, isLpcHotelOffer, isBedbankOffer, isExperienceOffer, isCruiseOffer, isLEHotel } from 'lib/offer/offerTypes'
import type { AgentHubGetOffersCommissionBody } from '../agentHubCommission'
import { DIRECT_TOUR } from 'constants/tours'
import type { actions } from '@luxuryescapes/contract-svc-agent'
import { mapBedbankPackageToAgentHubPayload, mapFlashPackageToAgentHubPayload, mapLPCPackageToAgentHubPayload } from './agentHubMap'
import { CHECKOUT_ITEM_TYPE_FLIGHT } from 'constants/checkout'
import { mapCruiseDepartureToAgentHubDateRangePayload, mapTourVariationToAgentHubDateRangePayload } from 'lib/agentHub/agentHubUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getDateChangeJourneyV2IdKey, getJourneyV2IdKey } from 'lib/flights/flightUtils'

type ServerAgentCommission = actions['calculateCartCommission']['responses']['200']['content']['application/json']['result']
type ServerOfferCommission = actions['calculateOffersCommission']['responses']['200']['content']['application/json']['result'][number]

type CommissionAdditionalInfo = {
  selectedCabinClass?: App.Checkout.FlightItem['selectedCabinClass']
  marketingAirlineCode?: string
  bundledWithTypes?: Array<'flight'>
  type?: App.Checkout.TourV2ExperienceItemType
  optionalExperienceId?: string
}

type CalculateCartCommissionBodyItem = {
  offerId: string
  type: string
  price: number
  location: Array<string>
  vendor: string
  transactionKey: string
  mx?: string
  bundledOffers?: Array<string>
  additionalInfo?: CommissionAdditionalInfo
  startDate?: string
  endDate?: string
  taxesAndFees?: number
}

type CalculateCartCommissionBody = {
  cartId: string
  items: Array<CalculateCartCommissionBodyItem>
  total: number
  transactionKey: string
}

function bundledWithFlightAdditionalInfo(): NonNullable<CalculateCartCommissionBodyItem['additionalInfo']> {
  return { bundledWithTypes: ['flight'] }
}

function getFlightMarketingAirlineCodeForCommission(journey: App.JourneyV2): string | undefined {
  return journey.flightGroup?.flights?.[0]?.marketingAirline?.code
}

function isJourneyV2InFlightsStore(journey: App.AnyJourney): journey is App.JourneyV2 {
  return journey.itemType === 'journeyV2' && 'flightGroup' in journey
}

function offerFlightCommissionAdditionalInfo(marketingAirlineCode: string | undefined): CommissionAdditionalInfo | undefined {
  if (!marketingAirlineCode) return undefined
  return { marketingAirlineCode }
}

function cartLegJourneyStoreKey(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): string | undefined {
  if (!item.searchId) return undefined
  return postPurchaseMode === 'flight-change-dates'
    ? item.reservationId
      ? getDateChangeJourneyV2IdKey(item.reservationId, item.searchId, leg.journeyId)
      : undefined
    : getJourneyV2IdKey(leg.journeyId, item.searchId, leg.fareFamily?.id)
}

function getJourneyV2ForCartLeg(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): App.JourneyV2 | undefined {
  const key = cartLegJourneyStoreKey(item, leg, postPurchaseMode)
  if (!key) return undefined
  const stored = journeysById[key]
  if (!stored || !isJourneyV2InFlightsStore(stored)) return undefined
  return stored
}

/** Same shape as offer commission: [arrivalCity, arrivalCountry, departureCity, departureCountry] per segment, flattened. */
function cartFlightCommissionLocationFromJourneyV2(journey: App.JourneyV2 | undefined): Array<string> {
  const segments = journey?.flightGroup?.flights
  if (!segments?.length) return []
  return segments.flatMap((flight) => [
    flight.arrivalCity,
    flight.arrivalCountry,
    flight.departureCity,
    flight.departureCountry,
  ])
}

function cartFlightCommissionLocationFromJourneyFlight(journeyFlight: App.JourneyFlight | undefined): Array<string> {
  const segments = journeyFlight?.flights
  if (!segments?.length) return []
  return segments.flatMap((seg) => [
    seg.arrivalDisplayNames?.primary ?? seg.arrivalAirportName,
    seg.arrivalCountry,
    seg.departingDisplayNames?.primary ?? seg.departingAirportName,
    seg.departingCountry,
  ])
}

function countryNameFromAirportCode(countryCode: string | undefined): string {
  if (!countryCode) return ''
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

function cartFlightCommissionLocationFromAirportCodes(
  arrivalAirportCode: string | undefined,
  arrivalAirportName: string | undefined,
  departureAirportCode: string | undefined,
  departureAirportName: string | undefined,
  airportsByCode: Map<string, App.Airport>,
): Array<string> {
  const arrivalAirport = arrivalAirportCode ? airportsByCode.get(arrivalAirportCode) : undefined
  const departureAirport = departureAirportCode ? airportsByCode.get(departureAirportCode) : undefined

  return [
    arrivalAirport?.name ?? arrivalAirportName ?? arrivalAirportCode ?? '',
    countryNameFromAirportCode(arrivalAirport?.countryCode),
    departureAirport?.name ?? departureAirportName ?? departureAirportCode ?? '',
    countryNameFromAirportCode(departureAirport?.countryCode),
  ]
}

function cartFlightCommissionLocationForCartLeg(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  legIndex: number,
  flightItemView: App.Checkout.FlightItemView,
  airportsByCode: Map<string, App.Airport>,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): Array<string> {
  const fromStore = cartFlightCommissionLocationFromJourneyV2(
    getJourneyV2ForCartLeg(item, leg, journeysById, postPurchaseMode),
  )
  if (fromStore.length > 0) return fromStore
  const viewFlight = flightItemView.flights[legIndex]
  const fromView = cartFlightCommissionLocationFromJourneyFlight(viewFlight?.journeyFlight)
  if (fromView.length > 0) return fromView
  return cartFlightCommissionLocationFromAirportCodes(
    leg.arrivalAirportCode,
    leg.arrivalAirportName,
    leg.departingAirportCode,
    leg.departingAirportName,
    airportsByCode,
  )
}

/** Grouped checkout line: unique journeys only (avoids duplicating segments when fixture legs share one journey id). */
function cartFlightCommissionLocationForGroupedFlightItem(
  flightItemView: App.Checkout.FlightItemView,
  airportsByCode: Map<string, App.Airport>,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): Array<string> {
  const seenKeys = new Set<string>()
  const fromStore: Array<string> = []
  for (const leg of flightItemView.item.flights) {
    const key = cartLegJourneyStoreKey(flightItemView.item, leg, postPurchaseMode)
    if (!key || seenKeys.has(key)) continue
    const stored = journeysById[key]
    if (!stored || !isJourneyV2InFlightsStore(stored)) continue
    seenKeys.add(key)
    fromStore.push(...cartFlightCommissionLocationFromJourneyV2(stored))
  }
  if (fromStore.length > 0) return fromStore

  const fromView = flightItemView.flights.flatMap((vf) => cartFlightCommissionLocationFromJourneyFlight(vf.journeyFlight))
  if (fromView.length > 0) return fromView

  const item = flightItemView.item
  const firstLeg = item.flights[0]
  const lastLeg = item.flights.at(-1)
  return cartFlightCommissionLocationFromAirportCodes(
    item.destinationAirportCode,
    lastLeg?.arrivalAirportName,
    item.originAirportCode,
    firstLeg?.departingAirportName,
    airportsByCode,
  )
}

function marketingAirlineCodeForCartLeg(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): string | undefined {
  const journey = getJourneyV2ForCartLeg(item, leg, journeysById, postPurchaseMode)
  return journey ? getFlightMarketingAirlineCodeForCommission(journey) : undefined
}

function flightCartAdditionalInfo(
  selectedCabinClass: App.Checkout.FlightItem['selectedCabinClass'],
  marketingAirlineCode: string | undefined,
): CommissionAdditionalInfo | undefined {
  if (!selectedCabinClass && !marketingAirlineCode) return undefined
  return {
    ...(selectedCabinClass && { selectedCabinClass }),
    ...(marketingAirlineCode && { marketingAirlineCode }),
  }
}

const NEW_TOUR_PRODUCT_TYPES = [PRODUCT_TYPE_TOUR_DELUXE, PRODUCT_TYPE_TOUR_PREMIUM, PRODUCT_TYPE_TOUR_ULTRA_LUX, PRODUCT_TYPE_TOUR_SIGNATURE_SERIES, PRODUCT_TYPE_TOUR_CUSTOM]

const getTourV2StartLocation = (item: App.Checkout.TourV2AccommodationOfferView) => {
  const variations = item.itemViews.map((x) => x.variation)
  if (variations.length === 0) return ['']
  return [variations[0]?.startLocation ?? '']
}

const sumViewItemTotal = (itemView: App.Checkout.AnyItemView): number => {
  const itemPrice = itemView.totals.price
  const isTourV2 = itemView.item.itemType === 'tourV2'
  const itemTotalPrice = isTourV2 ? itemView.item?.total ?? itemPrice : itemPrice
  const fullPrice = itemTotalPrice + (itemView.totals.surcharge ?? 0) + (itemView.totals.otherFees?.extraGuestSurcharge ?? 0)
  const fullPriceSubtractedPaidPrice = fullPrice - (itemView.totals.paidPrice ?? 0)
  return isTourV2 ? Math.max(fullPriceSubtractedPaidPrice, 0) : fullPrice
}
const sumViewItemsTotal = (items: Array<App.Checkout.AnyItemView>): number => {
  return sum(items, (i) => sumViewItemTotal(i))
}

const processExperienceItems = (experienceItemsView: Array<App.Checkout.ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []

  for (const item of experienceItemsView) {
    for (const ticket of item.ticketViews) {
      items.push({
        offerId: item.experienceId,
        type: OFFER_TYPE_EXPERIENCE,
        price: ticket.item.total ?? item.totals.price / (item.ticketViews.length ?? 1),
        location: [item.location ?? ''],
        vendor: '',
        transactionKey: ticket.item.transactionKey,
        startDate: item.bookingDate,
        endDate: item.bookingDate,
      })
    }
  }

  return items
}

const processTransferItems = (transferItemsView: Array<App.Checkout.TransferItemView> = []): Array<CalculateCartCommissionBodyItem> => {
  return transferItemsView.map((itemView) => ({
    offerId: itemView.item.experienceId,
    type: OFFER_TYPE_EXPERIENCE_TRANSFER,
    price: itemView.item.transfer.option?.price ?? 0,
    location: [],
    vendor: '',
    transactionKey: itemView.item.transactionKey,
    startDate: itemView.item.transfer.date,
    endDate: itemView.item.transfer.date,
  }))
}

const processCarHireItems = (carHireItemsView: Array<App.Checkout.CarHireItemView>): Array<CalculateCartCommissionBodyItem> => {
  return (carHireItemsView ?? []).map((itemView) => ({
    offerId: itemView.item.offerId,
    type: PRODUCT_TYPE_CAR_HIRE,
    price: itemView.item.price,
    location: [],
    vendor: '',
    transactionKey: itemView.item.transactionKey,
  }))
}

const processBedBankItems = (item: App.Checkout.BedbankAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const locations = [
    item?.offer?.property?.address.city ?? '',
    item?.offer?.property?.address.stateProvinceName ?? '',
    item.offer?.property?.address.countryName ?? '',
  ].filter(Boolean)

  const transactionKeyGroupedBySessionId: Record<string, string> = {}

  return item.itemViews.map((itemView) => {
    let transactionKey = itemView.item.transactionKey
    const sessionId = itemView.item.sessionId
    if (sessionId) {
      const transactionKeyGroup = transactionKeyGroupedBySessionId[sessionId]
      if (transactionKeyGroup) {
        transactionKey = transactionKeyGroup
      } else {
        transactionKeyGroupedBySessionId[sessionId] = transactionKey
      }
    }

    return {
      type: item.offer?.productType ?? '',
      offerId: item.offerId,
      price: sumViewItemTotal(itemView),
      location: locations,
      vendor: '',
      transactionKey,
      mx: itemView.rate?.mx,
      startDate: itemView.item.checkIn,
      endDate: itemView.item.checkOut,
      ...(itemView.item.isFlightBundle && {
        additionalInfo: bundledWithFlightAdditionalInfo(),
      }),
    }
  })
}

const processTourV2Items = (item: App.Checkout.TourV2AccommodationOfferView, allOptionalExperiences: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []

  const currentTourOptionalExperiences = allOptionalExperiences.filter((oe) => oe.item.offerId === item.offerId)
  const leahProductType = NEW_TOUR_PRODUCT_TYPES.includes(item.offer?.productType ?? '') ?
    DIRECT_TOUR :
    item.offer?.productType ?? ''

  items.push({
    type: leahProductType,
    offerId: item.offerId,
    price: sumViewItemsTotal(item.itemViews),
    location: getTourV2StartLocation(item),
    vendor: '',
    transactionKey: item.itemViews[0].item.transactionKey,
    startDate: item.itemViews[0].item.startDate,
    endDate: item.itemViews[0].item.endDate,
  })

  for (const optionalExperience of currentTourOptionalExperiences) {
    items.push(...processTourV2OptionalExperienceItems([optionalExperience]))
  }

  return items
}

const processCruiseItems = (item: App.Checkout.CruiseAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  return item.itemViews.map((itemView) => ({
    type: item.offer?.productType ?? '',
    offerId: item.offerId,
    price: sumViewItemTotal(itemView),
    location: [item.location],
    vendor: item.offer?.cruiseLine?.name ?? '',
    transactionKey: itemView.item.transactionKey,
    startDate: itemView.item.startDate,
    endDate: itemView.item.endDate,
    taxesAndFees: itemView.totals.taxesAndFees ?? 0,
  }))
}

const processAlwaysOnItems = (item: App.Checkout.LEAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const locations = unique((item?.offer?.locations ?? []).concat(item?.offer?.location ?? []))

  return item.itemViews.map((itemView) => ({
    offerId: item.offerId,
    type: item.offer?.productType ?? item.offerType,
    price: sumViewItemTotal(itemView),
    location: locations,
    vendor: item.offer?.vendorName ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.pkg?.roomRate?.mx && { mx: itemView.pkg?.roomRate?.mx }),
    startDate: item.startDate,
    endDate: item.endDate,
    ...(item.offer?.offerFlightsEnabled && { additionalInfo: bundledWithFlightAdditionalInfo() }),
  }))
}

const processFlightItems = (
  flightItemsView: Array<App.Checkout.FlightItemView>,
  accommodationItems: Array<App.Checkout.AccommodationOfferView>,
  airports: Array<App.Airport>,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []
  const airportsByCode = arrayToMap(airports, airport => airport.code)

  for (const flightItemView of flightItemsView) {
    const isTwoOneWays = flightItemView.viewType === 'TwoOneWays'
    const hasMultipleLegs = flightItemView.item.flights.length > 1
    const flightAddons = flightItemView.item.otherFees || {}
    const addonTotalPrice = sum(Object.values(flightAddons), (fee) => fee as number)

    const bundledOffers = accommodationItems?.flatMap(accommodation => accommodation.itemViews.filter(accommodationItemView => flightItemView.item.bundledItemIds?.includes(accommodationItemView.item.itemId))).map(offer => offer.item.offerId).filter(id => !!id)

    if (isTwoOneWays && hasMultipleLegs) {
      const allLegsAreDomesticAU = flightItemView.flights.every((fv) => {
        const leg0 = fv.journeyFlight?.flights?.[0]
        return leg0?.departingCountry === 'Australia' && leg0?.arrivalCountry === 'Australia'
      })
      const providers = flightItemView.item.flights.map(f => f.provider).filter(Boolean)
      const hasDifferentProviders = new Set(providers).size > 1

      const splitIntoPerLegCommissionLines =
        !allLegsAreDomesticAU || hasDifferentProviders

      if (splitIntoPerLegCommissionLines) {
        flightItemView.item.flights.forEach((flight, index) => {
          const marketingAirlineCode = marketingAirlineCodeForCartLeg(flightItemView.item, flight, journeysById, postPurchaseMode)
          const additionalInfo = flightCartAdditionalInfo(flightItemView.item.selectedCabinClass, marketingAirlineCode)
          const bookingLevelAddonForLeg = index === 0 ? addonTotalPrice : 0
          items.push({
            offerId: flight.journeyId,
            transactionKey: flightItemView.item.transactionKey,
            location: cartFlightCommissionLocationForCartLeg(
              flightItemView.item,
              flight,
              index,
              flightItemView,
              airportsByCode,
              journeysById,
              postPurchaseMode,
            ),
            price: (flight.cost ?? 0) + bookingLevelAddonForLeg,
            type: CHECKOUT_ITEM_TYPE_FLIGHT,
            vendor: flight.provider,
            ...(bundledOffers.length > 0 && { bundledOffers }),
            ...(additionalInfo && { additionalInfo }),
          })
        })
        continue
      }
    }

    const firstLeg = flightItemView.item.flights[0]
    const defaultMarketingAirlineCode = firstLeg ? marketingAirlineCodeForCartLeg(flightItemView.item, firstLeg, journeysById, postPurchaseMode) : undefined
    const defaultAdditionalInfo = flightCartAdditionalInfo(flightItemView.item.selectedCabinClass, defaultMarketingAirlineCode)
    items.push({
      offerId: flightItemView.item.flights?.[0]?.journeyId ?? '',
      transactionKey: flightItemView.item.transactionKey,
      location: cartFlightCommissionLocationForGroupedFlightItem(flightItemView, airportsByCode, journeysById, postPurchaseMode),
      price: flightItemView.totals.price + addonTotalPrice,
      type: CHECKOUT_ITEM_TYPE_FLIGHT,
      vendor: flightItemView.provider ?? flightItemView.item.flights[0]?.provider ?? '',
      ...(bundledOffers.length > 0 && { bundledOffers }),
      ...(defaultAdditionalInfo && { additionalInfo: defaultAdditionalInfo }),
    })
  }

  return items
}

const processDefaultItems = (item: App.Checkout.LEAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const locations = unique((item?.offer?.locations ?? []).concat(item?.offer?.location ?? []))

  return item.itemViews.map((itemView) => ({
    offerId: item.offerId,
    type: item.offer?.productType ?? item.offerType,
    price: sumViewItemTotal(itemView),
    location: locations,
    vendor: item.offer?.vendorName ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.pkg?.mx && { mx: itemView.pkg?.mx }),
    startDate: item.startDate,
    endDate: item.endDate,
    ...(item.offer?.offerFlightsEnabled && { additionalInfo: bundledWithFlightAdditionalInfo() }),
  }))
}

const processAccommodationItem = (item: App.Checkout.AccommodationOfferView, allOptionalExperiences: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  switch (item.offerType) {
    case OFFER_TYPE_TOUR:
      return processDefaultItems(item)
    case OFFER_TYPE_BED_BANK:
      return processBedBankItems(item)
    case OFFER_TYPE_TOUR_V2:
      return processTourV2Items(item, allOptionalExperiences)
    case OFFER_TYPE_CRUISE:
      return processCruiseItems(item)
    case OFFER_TYPE_ALWAYS_ON:
      return processAlwaysOnItems(item)
    default:
      return processDefaultItems(item)
  }
}

const processTourV2OptionalExperienceItems = (data: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  return data.map((item) => ({
    type: DIRECT_TOUR,
    // must be the id of the tour so that it applies the same commission as the tour
    offerId: item.item.purchasableOption.fkTourId,
    price: item.item.total ?? item.item.purchasableOption.fullPrice,
    location: [],
    vendor: '',
    startDate: item.item.date,
    endDate: item.item.date,
    transactionKey: item.item.transactionKey,
    additionalInfo: {
      type: item.item.itemType,
      optionalExperienceId: item.item.offerId,
    },
  }))
}

const getCommissionItems = (state: App.State): Array<CalculateCartCommissionBodyItem> => {
  const { accommodationItemsView, flightItemsView, tourV2ExperienceItemsView, experienceItemsView, transferItemsView, carHireItemsView } =
    getAllItemViewsByType(state).data
  const allOptionalExperiences = tourV2ExperienceItemsView?.data ?? []

  const experienceItems = processExperienceItems(experienceItemsView.data)
  const transferItems = processTransferItems(transferItemsView.data)
  const carHireItems = processCarHireItems(carHireItemsView.data)
  const flightItems = processFlightItems(
    flightItemsView.data,
    accommodationItemsView.data,
    state.geo.airports,
    state.flights.journeysById,
    findPostPurchaseCheckout(state.checkout.cart.mode),
  )
  const accommodationItems = accommodationItemsView.data.flatMap((item) => processAccommodationItem(item, allOptionalExperiences))

  // when checkout is type "tour-optional-extras", it allows the user to have an optional experience without the tour item in the checkout
  // so we must process them outside of the "tourV2" scope
  const isOptionalExperiencesCheckout = state.checkout.cart.mode === 'tour-optional-experiences'
  const tourOptionalExperiences = isOptionalExperiencesCheckout ? processTourV2OptionalExperienceItems(allOptionalExperiences) : []

  return [...experienceItems, ...transferItems, ...carHireItems, ...flightItems, ...accommodationItems, ...tourOptionalExperiences]
}

export const stateToCommissionOrder = (state: App.State): CalculateCartCommissionBody => {
  const items = getCommissionItems(state)
  return {
    items,
    cartId: state.checkout.cart.cartId,
    total: sum(items, (i) => i.price ?? 0),
    transactionKey: state.checkout.cart.transactionKey,
  }
}

export const agentHubCommissionMap = (serverCommission: ServerAgentCommission): App.AgentHubCommission => {
  return {
    promoCode: serverCommission.promoCode,
    itemsCommission: Object.fromEntries(
      Object.entries(serverCommission.itemsCommission).map(([key, value]) => {
        return [
          key,
          {
            commissionPercentage: value.commissionPercentage,
            itemDiscount: value.itemDiscount,
            includesTaxesAndFees: value.includesTaxesAndFees ?? true,
          },
        ]
      }),
    ),
  }
}

export const mapOfferCommission = (
  serverOfferCommission: ServerOfferCommission & {
    dateRanges: Array<{ id: string; startDate: string; endDate: string }>
    includesTaxesAndFees?: boolean
  },
): App.AgentHubOfferCommission => {
  return {
    offerId: serverOfferCommission.offerId,
    commissionPercentage: serverOfferCommission.commission,
    baseCommissionPercentage: serverOfferCommission.baseCommission,
    additionalCommissionsPercentage: serverOfferCommission.additionalCommissions,
    packages: serverOfferCommission.packages.map((pkg) => ({
      packageId: pkg.packageId,
      commissionPercentage: pkg.commission,
      baseCommissionPercentage: pkg.baseCommission,
      additionalCommissionsPercentage: pkg.additionalCommissions,
    })),
    minBaseCommission: serverOfferCommission.minBaseCommission,
    maxBaseCommission: serverOfferCommission.maxBaseCommission,
    dateRangesCommission: arrayToObject(serverOfferCommission.dateRanges, (dateRange) => dateRange.id),
    includesTaxesAndFees: serverOfferCommission.includesTaxesAndFees ?? true,
  }
}

export const mapOfferToAgentHubCommissionPayload = (offer: App.AgentHubOfferPayload, { offerRates = {}, bundledOffers = [] }: { offerRates?: Record<string, Array<App.BedbankRate>>, bundledOffers?: Array<string> } = {}): AgentHubGetOffersCommissionBody => {
  const mappedData: AgentHubGetOffersCommissionBody = match(offer)
    .when((data) => 'journey' in data, (flightWithJourney) => {
      const flightAdditionalInfo = offerFlightCommissionAdditionalInfo(
        getFlightMarketingAirlineCodeForCommission(flightWithJourney.journey),
      )
      return {
        offerId: flightWithJourney.journey.id,
        type: 'flight' as const,
        vendor: flightWithJourney.journey.provider,
        price: flightWithJourney.journey.price.all.totalRoundTripPrice,
        location: flightWithJourney.journey.flightGroup.flights.map(flight => [flight.arrivalCity, flight.arrivalCountry, flight.departureCity, flight.departureCountry]).flat(),
        packages: [],
        ...(bundledOffers.length > 0 && { bundledOffers }),
        ...(flightAdditionalInfo && { additionalInfo: flightAdditionalInfo }),
      }
    })
    .when(isCarHireOffer, (carOffer) => ({
      offerId: carOffer.id,
      type: carOffer.productType,
      vendor: carOffer.vendor.name,
      price: carOffer.price,
      location: [carOffer.pickUp.name, carOffer.dropOff.name],
      packages: [],
    }))
    .when(isBedbankOffer, (bedbankOffer) => {
      // packages are supposed to exist at this point.
      // this `packages?.[0]?` is just a safeguard because of an error happening when the user agent is Google Adbots
      const price = bedbankOffer.packages?.[0]?.sell?.value ?? 0
      return {
        type: bedbankOffer.productType,
        offerId: bedbankOffer.id,
        vendor: '',
        price,
        location: [bedbankOffer.property.address.city],
        packages: Object.keys(offerRates).length > 0 ? offerRates[Object.keys(offerRates)[0]].map(mapBedbankPackageToAgentHubPayload) : [],
      }
    })
    .when(isTourV2Offer, (tourOffer) => {
      const variation = Object.keys(tourOffer.variations)[0]
      const datePayload = Object.values(tourOffer.variations).flatMap(mapTourVariationToAgentHubDateRangePayload)
      return {
        offerId: tourOffer.id,
        type: NEW_TOUR_PRODUCT_TYPES.includes(tourOffer.productType) ? DIRECT_TOUR : tourOffer.productType,
        vendor: '',
        price: 0,
        location: [tourOffer.variations[variation]?.startLocation ?? ''],
        packages: [],
        dateRanges: datePayload,
      }
    })
    .when(isExperienceOffer, (expOffer) => ({
      offerId: expOffer.id,
      type: expOffer.productType !== OFFER_TYPE_EXPERIENCE_TRANSFER ? OFFER_TYPE_EXPERIENCE as App.OfferType : expOffer.productType,
      vendor: expOffer.vendor?.name ?? '',
      price: expOffer.price,
      location: [expOffer.location.name],
      packages: [],
    }))
    .when(isCruiseOffer, (cruiseOffer) => {
      const departures = cruiseOffer.departuresSummary || cruiseOffer.departures
      const departuresArray = Object.values(departures) as unknown as Array<typeof departures[string]>
      return {
        offerId: cruiseOffer.id,
        type: cruiseOffer.productType,
        vendor: cruiseOffer.cruiseLine?.name ?? '',
        price: cruiseOffer.mainDepartureDetails.lowestOverallPriceDetails.total,
        location: [cruiseOffer.departurePort ?? ''],
        packages: [],
        dateRanges: departuresArray.flatMap(val => val.map(mapCruiseDepartureToAgentHubDateRangePayload)),
      }
    })
    .otherwise((defaultOffer) => ({
      offerId: defaultOffer.id,
      type: defaultOffer.productType,
      vendor: defaultOffer.vendorName ?? '',
      price: defaultOffer.lowestPricePackage?.price ?? 0,
      location: [defaultOffer.location ?? ''],
      packages: getHotelPackages(defaultOffer),
      ...(defaultOffer.offerFlightsEnabled && {
        additionalInfo: bundledWithFlightAdditionalInfo(),
      }),
    }))

  return {
    offerId: mappedData.offerId,
    location: mappedData.location,
    price: mappedData.price,
    type: mappedData.type,
    vendor: mappedData.vendor,
    packages: mappedData.packages,
    ...(mappedData.bundledOffers && { bundledOffers: mappedData.bundledOffers }),
    ...(mappedData.dateRanges && { dateRanges: mappedData.dateRanges }),
    ...(mappedData.additionalInfo && { additionalInfo: mappedData.additionalInfo }),
  }
}

const getHotelPackages = (offer: App.Offer | App.TourOfferSummary): AgentHubGetOffersCommissionBody['packages'] => {
  return match(offer)
    .when(isLpcHotelOffer, (lpcOffer) => {
      if (!lpcOffer?.packages) {
        return []
      }

      return lpcOffer.packages.filter(pkg => !!pkg.roomRate?.mx).map(mapLPCPackageToAgentHubPayload)
    })
    .when(isLEHotel, (hotelOffer) => {
      if (!hotelOffer?.packages) {
        return []
      }

      return hotelOffer.packages.filter(pkg => !!pkg.mx).map(mapFlashPackageToAgentHubPayload)
    })
    .otherwise(() => [])
}
